import {
	BottomSheetFooter,
	type BottomSheetFooterProps,
} from "@gorhom/bottom-sheet";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Text } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useProficiencyIconMap } from "../../../hooks/useProficiencyIconMap";
import { useAppTheme } from "../../../useAppTheme";
import { useDevice } from "../../../useDevice";
import BottomSheetModalWrapper from "../../BottomSheetModalWrapper";
import Box from "../../Box";
import Button from "../../Button";
import DialogWrapper from "../../DialogWrapper";
import { SessionReportReviewAnswersFilterSections } from "./SessionReportReviewAnswersFilterSections";
import {
	formatFilterLabels,
	resolveReviewAnswersFilterChange,
} from "./helpers";
import type {
	ReviewAnswersAction,
	SessionReportReviewAnswersDialogProps,
} from "./types";
import { useSessionReportReviewAnswersFilters } from "./useSessionReportReviewAnswersFilters";

const SHEET_SNAP_POINT = "90%";
const SHEET_FOOTER_CLEARANCE = 220;

type VisibleProps = Omit<SessionReportReviewAnswersDialogProps, "visible">;

const VisibleSessionReportReviewAnswersDialog = ({
	sessionItems,
	loading,
	onClose,
	onFilterChange,
	onSubmit,
}: VisibleProps) => {
	const { t, i18n } = useTranslation();
	const theme = useAppTheme();
	const insets = useSafeAreaInsets();
	const { isMobile } = useDevice();
	const proficiencyIconMap = useProficiencyIconMap();
	const { view, toggle } = useSessionReportReviewAnswersFilters({
		sessionItems,
		proficiencyIconMap,
	});

	const handleToggle = useCallback(
		(action: ReviewAnswersAction) => {
			toggle(action);
			onFilterChange?.(resolveReviewAnswersFilterChange({ action, view }));
		},
		[toggle, onFilterChange, view],
	);

	const summaryText = useMemo(() => {
		const count = view.selectedItems.length;
		if (count === 0) {
			return t("report.reviewAnswersDialog.summaryNone");
		}

		if (view.isAllSelected) {
			return t("report.reviewAnswersDialog.summaryAll", { count });
		}

		return t("report.reviewAnswersDialog.summaryFiltered", {
			count,
			filters: formatFilterLabels({
				labels: view.selectedLabels,
				locale: i18n.language,
				t,
			}),
		});
	}, [
		i18n.language,
		t,
		view.isAllSelected,
		view.selectedItems.length,
		view.selectedLabels,
	]);

	const header = (
		<Box paddingHorizontal={24} paddingVertical={16} alignItems="center">
			<Text variant="headlineSmall">
				{t("report.reviewAnswersDialog.title")}
			</Text>
		</Box>
	);

	const footer = (
		<Box
			borderTopWidth={1}
			borderColor={theme.colors.outlineVariant}
			paddingHorizontal={24}
			paddingTop={16}
			paddingBottom={isMobile ? 16 + insets.bottom : 16}
			gap={16}
			backgroundColor={theme.colors.surface}
		>
			{summaryText ? (
				<Box
					paddingHorizontal={14}
					paddingVertical={12}
					borderWidth={1}
					borderRadius={theme.roundness * 2.5}
					borderColor={theme.colors.surfaceVariant}
					backgroundColor={theme.colors.elevation.level1}
				>
					<Text variant="bodyMedium">{summaryText}</Text>
				</Box>
			) : null}
			<Button
				mode="contained"
				icon="text-search"
				disabled={Boolean(loading) || view.selectedItems.length === 0}
				loading={loading}
				onPress={() =>
					onSubmit(view.selectedItems, {
						selectedLabels: view.selectedLabels,
						isAllSelected: view.isAllSelected,
					})
				}
			>
				{t("report.reviewAnswersDialog.reviewAnswers", {
					count: view.selectedItems.length,
				})}
			</Button>
		</Box>
	);

	const renderSheetFooter = useCallback(
		(footerProps: BottomSheetFooterProps) => (
			<BottomSheetFooter {...footerProps}>{footer}</BottomSheetFooter>
		),
		[footer],
	);

	if (isMobile) {
		return (
			<BottomSheetModalWrapper
				showModal={true}
				snapIndex={0}
				snapPoint={SHEET_SNAP_POINT}
				animateOnMount
				enablePanDownToClose
				enableDismissOnClose
				onDismiss={onClose}
				backdropAppearsOnIndex={0}
				backdropDisappearsOnIndex={-1}
				backgroundStyle={{ backgroundColor: theme.colors.surface }}
				footerComponent={renderSheetFooter}
				contentContainerStyle={{
					paddingHorizontal: 0,
					paddingTop: 0,
					paddingBottom: SHEET_FOOTER_CLEARANCE,
				}}
			>
				{header}
				<SessionReportReviewAnswersFilterSections
					view={view}
					loading={loading}
					toggle={handleToggle}
					presentation="sheet"
				/>
			</BottomSheetModalWrapper>
		);
	}

	return (
		<DialogWrapper
			visible={true}
			handleClose={onClose}
			showCloseButton
			maxWidth={560}
			width="90%"
		>
			{header}
			<SessionReportReviewAnswersFilterSections
				view={view}
				loading={loading}
				toggle={handleToggle}
			/>
			{footer}
		</DialogWrapper>
	);
};

export const SessionReportReviewAnswersDialog = ({
	visible,
	...props
}: SessionReportReviewAnswersDialogProps) => {
	if (!visible) {
		return null;
	}

	return <VisibleSessionReportReviewAnswersDialog {...props} />;
};
